import React, { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeletons } from "@/components/common/EmptyState";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useResource } from "@/hooks/useResource";
import { useApp } from "@/context/AppContext";
import api, { fileUrl, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PushPin, Plus, UploadSimple, Trash, ShareNetwork, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Inspiration() {
  const { items, isLoading, create, update, remove } = useResource("moodboards");
  const { brands } = useApp();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [active, setActive] = useState(null); // board being edited
  const [dragIdx, setDragIdx] = useState(null);
  const [newBoard, setNewBoard] = useState(false);
  const [form, setForm] = useState({});
  const [confirm, setConfirm] = useState(null);

  const openBoard = (b) => setActive(b);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["moodboards"] });

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !active) return;
    try {
      const uploaded = [];
      for (const f of files) { const r = await uploadFile(f, { tags: "moodboard" }); uploaded.push({ id: r.id, url: r.url }); }
      const items2 = [...(active.items || []), ...uploaded];
      await update.mutateAsync({ id: active.id, body: { items: items2 } });
      setActive({ ...active, items: items2 }); toast.success("Images added");
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };
  const removeImg = async (imgId) => {
    const items2 = active.items.filter((x) => x.id !== imgId);
    await update.mutateAsync({ id: active.id, body: { items: items2 } });
    setActive({ ...active, items: items2 });
  };
  const reorder = async (from, to) => {
    const arr = [...active.items]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    await update.mutateAsync({ id: active.id, body: { items: arr } });
    setActive({ ...active, items: arr });
  };
  const saveNotes = async (notes) => { await update.mutateAsync({ id: active.id, body: { notes } }); setActive({ ...active, notes }); };
  const share = async (b) => { await navigator.clipboard.writeText(`${window.location.origin}/inspiration#${b.id}`); toast.success("Board link copied"); };
  const createBoard = async () => {
    if (!form.name?.trim()) return toast.error("Name required");
    await create.mutateAsync({ name: form.name, brand: form.brand || brands[0]?.name, accent: "#7C3AED", notes: form.notes || "", tags: (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean), items: [] });
    setNewBoard(false); setForm({}); toast.success("Board created");
  };
  const delBoard = async (b) => { try { await remove.mutateAsync(b.id); toast.success("Deleted"); if (active?.id === b.id) setActive(null); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } setConfirm(null); };

  return (
    <div>
      <input ref={fileRef} type="file" multiple hidden accept="image/*" onChange={addImages} data-testid="moodboard-file-input" />
      <PageHeader title="Inspiration Boards" subtitle="Mood boards & saved ideas — upload, arrange, share" icon={PushPin}
        actions={<Button onClick={() => setNewBoard(true)} data-testid="add-btn" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><Plus size={16} weight="bold" className="mr-1" /> New Board</Button>} />

      {isLoading ? <Skeletons /> : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((b) => (
            <div key={b.id} data-testid="moodboard-card" className="group overflow-hidden rounded-2xl border border-border bg-surface-raised">
              <div className="grid grid-cols-2 gap-0.5">
                {(b.items || []).slice(0, 4).map((im) => <img key={im.id} src={fileUrl(im.url)} alt="" className="h-24 w-full object-cover" />)}
                {(b.items || []).length === 0 && <div className="col-span-2 flex h-24 items-center justify-center text-muted-foreground">Empty board</div>}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: b.accent }} /><p className="font-display font-bold">{b.name}</p></div>
                <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{b.brand} · {(b.items || []).length} images</p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="rounded-full" onClick={() => openBoard(b)} data-testid="open-board">Open</Button>
                  <button onClick={() => share(b)} data-testid="share-board" className="rounded-lg p-1.5 text-muted-foreground hover:text-primary"><ShareNetwork size={16} /></button>
                  <button onClick={() => setConfirm(b)} data-testid="delete-board" className="ml-auto rounded-lg p-1.5 text-muted-foreground hover:text-brand-pink"><Trash size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Board editor */}
      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl" data-testid="board-editor">
          {active && (<>
            <SheetHeader><SheetTitle className="font-display text-xl">{active.name}</SheetTitle></SheetHeader>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={() => fileRef.current?.click()} data-testid="board-upload" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><UploadSimple size={14} className="mr-1" /> Add images</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => share(active)}><ShareNetwork size={14} className="mr-1" /> Share</Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Drag images to rearrange.</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {(active.items || []).map((im, idx) => (
                <div key={im.id} draggable onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragIdx !== null && dragIdx !== idx) reorder(dragIdx, idx); setDragIdx(null); }}
                  data-testid="board-image" className="group/img relative cursor-grab overflow-hidden rounded-xl active:cursor-grabbing">
                  <img src={fileUrl(im.url)} alt="" className="h-28 w-full object-cover" />
                  <button onClick={() => removeImg(im.id)} data-testid="remove-image" className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white opacity-0 backdrop-blur transition-opacity group-hover/img:opacity-100"><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
              <Textarea defaultValue={active.notes} onBlur={(e) => saveNotes(e.target.value)} className="mt-1 rounded-xl" data-testid="board-notes" />
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">{(active.tags || []).map((t) => <span key={t} className="rounded-full bg-secondary px-2.5 py-1 text-xs">#{t}</span>)}</div>
          </>)}
        </SheetContent>
      </Sheet>

      {/* New board */}
      <Sheet open={newBoard} onOpenChange={setNewBoard}>
        <SheetContent side="right" className="w-full sm:max-w-md" data-testid="new-board-drawer">
          <SheetHeader><SheetTitle className="font-display text-xl">New Mood Board</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4">
            <div><Label className="text-xs uppercase text-muted-foreground">Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="board-name" /></div>
            <div><Label className="text-xs uppercase text-muted-foreground">Brand</Label><select value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="h-10 w-full rounded-xl border border-input bg-surface-raised px-3 text-sm"><option value="">Select…</option>{brands.map((b) => <option key={b.id}>{b.name}</option>)}</select></div>
            <div><Label className="text-xs uppercase text-muted-foreground">Tags (comma separated)</Label><Input value={form.tags || ""} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="rounded-xl" /></div>
            <div><Label className="text-xs uppercase text-muted-foreground">Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl" /></div>
            <Button onClick={createBoard} data-testid="create-board" className="w-full rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white">Create Board</Button>
          </div>
        </SheetContent>
      </Sheet>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title="Delete this board?" onConfirm={() => delBoard(confirm)} />
    </div>
  );
}
